import React, {useState, useCallback, useMemo, lazy, Suspense, CSSProperties} from 'react';
import {isAprilFool} from "../../utils/Utils.ts";


const chanceMulti = 0.1;
const effects = [
    { class: "drunk-icon", chance: 0.1 },
    { class: "melting-icon", chance: 0.1 },
    { class: "glitch-icon", chance: 0.1 },
    { class: "crazy-spin", chance: 0.1 },
    { class: "grow-shrink", chance: 0.1 },
    { class: "blurry-icon", chance: 0.1 },
    { class: "flipped", chance: 0.1 },
    { class: "fade-out", chance: 0.1},
    { class: "bouncing-icon", chance: 0.1 }
];
function getRandomEffect() {
    const delay = Math.floor(Math.random() * 5);
    const randomValue = Math.random();
    let cumulativeChance = 0;
    for (const effect of effects) {
        cumulativeChance += (effect.chance*chanceMulti);
        if (randomValue < cumulativeChance) {
            return effect.class + ' delay-'+delay;
        }
    }
    return null; // No effect applied
}

interface IconProps {
    icon: string;
    size?: string | number;
    activateHover?: boolean;
    forceHover?: boolean;
    rotate?: number;
    color?: CSSProperties['color'];
    hoverColor?: CSSProperties['color'];
    disabled?: boolean;
    onClick?: () => void;
    debug?: boolean;
    style?: CSSProperties;
    className?: string;
    ref?: React.RefObject<unknown>;
}

const Icon = React.memo((
    {
        ref,
        icon,
        size = '1em',

        // per attivare l'uso dell'hover anche senza onClick
        activateHover = false,

        // per ereditare lo stato hover dal padre
        forceHover = false,

        rotate = 0,

        // colore primario
        color = 'currentColor',

        // colore primario per l'hover
        hoverColor = '#e9613d',

        disabled = false,
        onClick,

        // INFO DI DEBUG
        debug = false,

        style = {},
        className = '',
        ...props
    }: IconProps
) => {
    const [isHover, setIsHover] = useState(false);
    const hasOnClick = typeof onClick === 'function';
    const isClickable = useMemo(() => {
        return hasOnClick && !disabled
    }, [hasOnClick, disabled]);

    const autoHover = useMemo(() => !disabled && (activateHover || hasOnClick), [activateHover, hasOnClick, disabled]);

    const onEnter = useCallback(() => {
        if (autoHover) {
            setIsHover(true);
        }
    }, [autoHover, setIsHover]);
    const onLeave = useCallback(() => {
        if (autoHover) {
            setIsHover(false);
        }
    }, [autoHover, setIsHover]);

    const I = useMemo(() => {
        return lazy(() =>
            import(`../../assets/icons/${icon}.jsx`).catch((error) => {
                console.error(`Failed to load icon: ${icon}`, error);
                return { default: () => <div className="icon-fallback" /> };
            })
        );
    }, [icon]);

    const memoStyle = useMemo(() => {
        style = {...style};
        style.fontSize = size as string;
        style.transform = 'rotate(' + rotate + 'deg)';

        if (disabled) {
            style.opacity = '0.6';
        }
        if (isClickable) {
            style.cursor = 'pointer';
        }
        return style;
    }, [style, isClickable]);

    const [fill] = useMemo(() => {
        let fill;
        if (forceHover || (autoHover && isHover && !disabled)) {
            // HOVER
            fill = hoverColor;
        } else {
            fill = color;
        }
        return [fill];
    }, [forceHover, autoHover, isHover, disabled, color, hoverColor]);

    if(isAprilFool) {
        className += ' '+getRandomEffect();
    }

    return (
        <Suspense fallback={<div className="icon-loading" style={memoStyle}/>}>
            <I
                ref={ref}
                onMouseEnter={onEnter}
                onMouseLeave={onLeave}
                className={className}
                style={memoStyle}
                onClick={disabled ? undefined : onClick}
                {...props}
                fill={fill}
            />
        </Suspense>);
});

export default Icon;
